import { AlertTriangle } from 'lucide-react';
import { formatCurrency } from '@utils/formatters';
import { clsx } from 'clsx';

const DailyBalanceTimeline = ({ dailyBalances, onDayClick, selectedDate }) => {
  if (!dailyBalances || dailyBalances.length === 0) {
    return (
      <p className="text-center text-gray-500 dark:text-gray-400 py-8">
        Nenhum dado de saldo disponível para o período.
      </p>
    );
  }

  const negativeDays = dailyBalances.filter(d => d.isNegative);
  const minBalance = Math.min(...dailyBalances.map(d => d.balance));
  const maxBalance = Math.max(...dailyBalances.map(d => d.balance));

  // Agrupar por semana
  const weeks = [];
  let currentWeek = [];

  dailyBalances.forEach((day, index) => {
    const dayOfWeek = new Date(day.date + 'T12:00:00').getDay();

    if (dayOfWeek === 0 && currentWeek.length > 0) {
      weeks.push(currentWeek);
      currentWeek = [];
    }

    currentWeek.push(day);

    if (index === dailyBalances.length - 1 && currentWeek.length > 0) {
      weeks.push(currentWeek);
    }
  });

  const getBalanceColor = (balance) => {
    if (balance < 0) return 'bg-red-500';
    if (balance < minBalance + (maxBalance - minBalance) * 0.25) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const formatDayOfWeek = (dateStr) => {
    const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    return days[new Date(dateStr + 'T12:00:00').getDay()];
  };

  const formatDayNumber = (dateStr) => {
    return new Date(dateStr + 'T12:00:00').getDate();
  };

  return (
    <div>
      {/* Alertas */}
      {negativeDays.length > 0 && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-600 dark:text-red-400">
            {negativeDays.length} dia(s) com saldo negativo neste período
          </span>
        </div>
      )}

      {/* Legenda */}
      <div className="flex items-center gap-4 mb-4 text-xs text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>
          Positivo
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-yellow-500 inline-block"></span>
          Baixo
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span>
          Negativo
        </span>
      </div>

      {/* Grid de Dias */}
      <div className="space-y-2">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="flex gap-1">
            {week.map((day) => (
              <button
                key={day.date}
                onClick={() => onDayClick?.(day.date)}
                className={clsx(
                  'flex-1 p-2 rounded-lg transition-all text-center',
                  selectedDate === day.date
                    ? 'ring-2 ring-primary-500 ring-offset-2 dark:ring-offset-gray-900 bg-primary-50 dark:bg-primary-900/20'
                    : day.transactions > 0
                      ? 'bg-gray-100 dark:bg-gray-700 hover:ring-2 hover:ring-gray-300 dark:hover:ring-gray-600'
                      : 'bg-gray-50 dark:bg-gray-800 hover:ring-2 hover:ring-gray-200 dark:hover:ring-gray-700'
                )}
              >
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDayOfWeek(day.date)}
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {formatDayNumber(day.date)}
                </p>
                <div className={clsx(
                  'w-2 h-2 rounded-full mx-auto mt-1',
                  getBalanceColor(day.balance)
                )} />
                <p className={clsx(
                  'text-xs mt-1 font-medium',
                  day.isNegative ? 'text-red-600' : 'text-gray-600 dark:text-gray-400'
                )}>
                  {formatCurrency(day.balance).replace('R$', '').trim()}
                </p>
                {day.transactions > 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {day.transactions} mov.
                  </p>
                )}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Resumo */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Saldo Mínimo</p>
          <p className={clsx(
            'text-sm font-semibold',
            minBalance < 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'
          )}>
            {formatCurrency(minBalance)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Saldo Máximo</p>
          <p className="text-sm font-semibold text-green-600">
            {formatCurrency(maxBalance)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Saldo Final</p>
          <p className={clsx(
            'text-sm font-semibold',
            dailyBalances[dailyBalances.length - 1]?.isNegative
              ? 'text-red-600'
              : 'text-gray-900 dark:text-white'
          )}>
            {formatCurrency(dailyBalances[dailyBalances.length - 1]?.balance || 0)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DailyBalanceTimeline;
